import { Checkbox, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useGradingScale } from 'lib/hooks/useGradingScale'
import { getPriceFormat } from 'lib/utils'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InventoryArticle, needsGradeCompletion } from 'shared'

export interface CompleteDataInGradedCardsProps {
  incomingStock: InventoryArticle[]
  handleConfirmGradedCards: (newIncomingStock: InventoryArticle[]) => void
}

export const CompleteDataInGradedCards = ({
  incomingStock,
  handleConfirmGradedCards,
}: CompleteDataInGradedCardsProps): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const { gradesFor, loading, error: gradingScaleFailed } = useGradingScale()
  const [workingIncomingStock, setWorkingIncomingStock] = useState<InventoryArticle[]>(
    _.cloneDeep(incomingStock)
  )

  /**
   * I need to maintain a fixed list of displayed cards (i.e., those with a gradingCompany but no grade at mount time),
   * and not recalculate this filter with each rendering.
   * To do this, I save the indexes (or IDs) of the items "to be corrected" at the beginning,
   * and use them to decide which rows to display, even if their data changes in workingIncomingStock.
   * With this approach, the table will only show the initially incomplete rows,
   * and nothing will disappear as the user interacts.
   */
  const articlesNeedingGrade = useMemo(() => {
    return incomingStock
      .map((a, i) => ({ article: a, index: i }))
      .filter(({ article }) => needsGradeCompletion(article))
      .map(({ index }) => index)
  }, [incomingStock])

  const updateRow = (index: number, newGrade: string) => {
    const copyIncomingStock = [...workingIncomingStock]
    copyIncomingStock[index].grade = newGrade

    setWorkingIncomingStock(copyIncomingStock)
  }

  // A graded article is missing its grade when the field is undefined OR blanked
  // out (empty string) — both must block confirmation even if "Is Graded" is checked.
  const hasMissingGrades = workingIncomingStock.some(
    (article) =>
      article.gradingCompany !== undefined &&
      (article.grade === undefined || `${article.grade}`.trim() === '')
  )

  // Nothing else is checked here any more: the grade can only come from the list its grading company
  // can actually issue, so there is no shape left to correct and no range left to refuse.
  const confirmUpdates = () => {
    if (hasMissingGrades) {
      enqueueSnackbar(t('inventory.setValueToAllArticles'), {
        variant: 'error',
      })
      return
    }

    handleConfirmGradedCards(_.cloneDeep(workingIncomingStock))
  }

  const updateAsRegular = (index: number) => {
    const copyIncomingStock = [...workingIncomingStock]
    copyIncomingStock[index].gradingCompany = undefined

    setWorkingIncomingStock(copyIncomingStock)
  }

  return (
    <div className="py-4 p-md-5 overflow-auto">
      <h3 className="mb-3">{t('addArticles.missingGrade')}</h3>
      <table className="graded-cards-table" style={{ minWidth: '740px' }}>
        <thead>
          <tr className="fw-bold">
            <td>
              <h6>{t('card.card')}</h6>
            </td>
            <td>
              <h6>{t('common.price')}</h6>
            </td>
            <td>
              <h6>{t('common.comment')}</h6>
            </td>
            <td>
              <h6>{t('stockPricing.gradingCompany')}</h6>
            </td>
            <td>
              <h6>{t('stockPricing.grade')}</h6>
            </td>
            <td style={{ textAlign: 'center' }}>{t('stockPricing.isGraded')}</td>
          </tr>
        </thead>
        <tbody>
          {articlesNeedingGrade.length > 0 ? (
            // index is the position in the whole incoming stock and is what the edits are written
            // against; row is the position in this table, which is what the test ids count.
            articlesNeedingGrade.map((index, row) => {
              const article = workingIncomingStock[index]
              return (
                <tr key={index} className="grading-cards-row">
                  <td style={{ width: '300px' }}>
                    <div className="d-flex flex-column justify-content-start">
                      <span>{article.card.name}</span>
                      <span>
                        {article.card.expansion} - {article.card.set} - {article.card.cn}
                      </span>
                    </div>
                  </td>
                  <td style={{ width: '120px' }}>{getPriceFormat(article.price, user.currency)}</td>
                  <td style={{ width: '300px' }}>{article.comment}</td>
                  <td style={{ width: '300px' }}>{article.gradingCompany}</td>
                  <td style={{ width: '300px' }}>
                    <Autocomplete
                      options={gradesFor(article.gradingCompany)}
                      value={article.grade ? article.grade : null}
                      disabled={!article.gradingCompany || loading}
                      // The grades come from the server, and with none of them there is nothing to pick: the
                      // field says so rather than looking merely empty, since the way out is a reload and the
                      // seller has no way of guessing that from an empty list.
                      noOptionsText={
                        gradingScaleFailed ? t('inventory.gradesNotLoaded') : undefined
                      }
                      // Typing the grade and pressing enter is enough: this table can be hundreds of
                      // rows long after an onboarding, and the mouse is the slow part.
                      autoHighlight
                      onChange={(_event, newValue) => updateRow(index, newValue ?? '')}
                      // The testid sits on the input rather than on the root, so the system tests keep
                      // finding the field the way they did when this was a plain text field.
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            gradingScaleFailed
                              ? t('inventory.gradesNotLoaded')
                              : t('inventory.insertGradeValue')
                          }
                          inputProps={{ ...params.inputProps, 'data-testid': `grade-${row}` }}
                        />
                      )}
                      renderOption={(option) => (
                        <li data-testid={`select-grade-${row}-${option}`}>{option}</li>
                      )}
                    />
                  </td>
                  <td style={{ width: '150px', textAlign: 'center' }}>
                    <Checkbox
                      data-testid="is-graded"
                      checked={article.gradingCompany !== undefined}
                      onChange={() => {
                        const wasChecked = article.gradingCompany !== undefined

                        if (wasChecked) {
                          updateAsRegular(index)
                        } else {
                          const originalGrading = incomingStock[index].gradingCompany
                          if (originalGrading) {
                            const updated = [...workingIncomingStock]
                            updated[index].gradingCompany = originalGrading
                            setWorkingIncomingStock(updated)
                          }
                        }
                      }}
                    />
                  </td>
                </tr>
              )
            })
          ) : (
            <tr>
              <p>{t('addArticles.gradedCardsNoActions')}</p>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-3 d-flex w-100 justify-content-end">
        <Button
          data-testid="confirm-graded-cards"
          variant="contained"
          disabled={hasMissingGrades}
          onClick={confirmUpdates}
        >
          Confirm
        </Button>
      </div>
    </div>
  )
}
